import React from 'react';
import styled from 'styled-components';
import Reveal from '../components/Reveal';
import { blob } from '../animations';
import { industries } from '../content';

const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.section} ${({ theme }) => theme.container.gutter};
  background: ${({ theme }) => theme.colors.ink};
  color: ${({ theme }) => theme.colors.paper};
`;

const Blob = styled.div`
  position: absolute;
  top: -10%;
  right: -4%;
  width: 32vw;
  height: 32vw;
  background: radial-gradient(circle, ${({ theme }) => theme.colors.accent}, transparent 70%);
  filter: blur(40px);
  opacity: 0.4;
  ${blob(16, 25)};
`;

const Inner = styled.div`
  position: relative;
  z-index: 2;
  max-width: ${({ theme }) => theme.container.maxWidth};
  margin: 0 auto;
`;

const Header = styled(Reveal)`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: clamp(24px, 4vw, 64px);
  flex-wrap: wrap;
  margin-bottom: clamp(44px, 6vw, 80px);
`;

const HeaderCopy = styled.div`
  flex: 0 1 auto;
`;

const Kicker = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.76rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.sky};
  margin-bottom: 18px;
`;

/* `ch` resolves against this element's own font, so the cap belongs here and not on the wrapper. */
const Title = styled.h2`
  max-width: 12ch;
  font-size: clamp(2rem, 4.6vw, 3.9rem);
  line-height: 1;
  letter-spacing: -0.025em;
  font-weight: 700;

  em {
    color: ${({ theme }) => theme.colors.sky};
  }
`;

const Intro = styled.p`
  max-width: 40ch;
  color: ${({ theme }) => theme.colors.onInk.text};
  font-size: 1.08rem;
  line-height: 1.55;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(48px, 7vw, 90px);
`;

const Row = styled(Reveal)`
  display: grid;
  grid-template-columns: 0.8fr 2.2fr;
  gap: clamp(24px, 4vw, 64px);

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

const Name = styled.h3`
  font-family: ${({ theme }) => theme.fonts.serif};
  font-style: italic;
  font-weight: 400;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  line-height: 1.05;
  color: ${({ theme }) => theme.colors.sky};
`;

const Blurb = styled.p`
  margin-top: 16px;
  color: ${({ theme }) => theme.colors.onInk.muted};
  font-size: 0.98rem;
  line-height: 1.55;
`;

const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: clamp(16px, 2vw, 28px);
`;

const Feature = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.onInk.line};
`;

const FeatureTitle = styled.h4`
  font-size: 1.12rem;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const FeatureText = styled.p`
  color: ${({ theme }) => theme.colors.onInk.muted};
  font-size: 0.92rem;
  line-height: 1.55;
`;

const Industries: React.FC = () => (
  <Section id="industries">
    <Blob aria-hidden />
    <Inner>
      <Header>
        <HeaderCopy>
          <Kicker>02 / Industry expertise</Kicker>
          <Title>
            Depth where it <em>matters</em>.
          </Title>
        </HeaderCopy>
        <Intro>
          Specialised knowledge across multiple sectors, so we deliver solutions that fit the real
          challenges of your industry.
        </Intro>
      </Header>

      <Rows>
        {industries.map(({ id, name, blurb, delay, features }) => (
          <Row key={id} id={id} delay={delay}>
            <div>
              <Name>{name}</Name>
              <Blurb>{blurb}</Blurb>
            </div>
            <Features>
              {features.map(feature => (
                <Feature key={feature.title}>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureText>{feature.desc}</FeatureText>
                </Feature>
              ))}
            </Features>
          </Row>
        ))}
      </Rows>
    </Inner>
  </Section>
);

export default Industries;
